import React, { useCallback, useMemo } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  getGeoblockLines,
  getSmartlistHref,
  smartlistColumns,
} from "./tableParams/smartlistColumns";
import SmartlistAPI from "../../../api/SmartlistAPI";
import ItemStatus from "../../../utils/ItemStatus";
import DataTable from "../../dataTable/DataTable";
import Breadcrumbs from "../../utils/Breadcrumbs";
import CappedPage from "../CappedPage";

import type { JSX, ReactElement } from "react";

import type { SmartlistRow } from "./tableParams/smartlistColumns.types";
import type { SmartlistType } from "../../../api/MediaAPI.types";
import type { AppState } from "../../../reducers";
import type { SmartlistSegment } from "../../routes/routes.types";
import type { BreadcrumbItem } from "../../utils/Breadcrumbs";

export const GET_SMARTLIST_LIST_QUERY_KEY = "getSmartlistIndexList";

export interface GenericSmartlistListProps {
  smartlistTypes: SmartlistType[] | SmartlistType;
  breadcrumbs: BreadcrumbItem[];
  tableTitle: string;
  createButton: ReactElement;
  deleteTitle: string;
  getDeleteBody: (label: string) => string;
  deleteCompleteText: string;
  editUrlPrefix: SmartlistSegment;
}

function GenericSmartlistList({
  smartlistTypes,
  breadcrumbs,
  tableTitle,
  createButton,
  deleteTitle,
  getDeleteBody,
  deleteCompleteText,
  editUrlPrefix,
}: GenericSmartlistListProps): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t } = useTranslation("editor-smartlist");
  const queryClient = useQueryClient();
  const history = useHistory();

  const countries = useSelector(
    (state: AppState) => state.fetchReducer.countries,
  );

  const getSmartlistIndexListQuery = SmartlistAPI.generateQuery({
    fn: SmartlistAPI.getSmartlistIndexList,
    params: [{ projectSlug, smartlistType: smartlistTypes }],
  });

  const {
    data: smartlistIndexList,
    isLoading,
    isError,
    refetch,
  } = useQuery(getSmartlistIndexListQuery);

  const smartlistRowList = useMemo(
    (): SmartlistRow[] =>
      (smartlistIndexList?.data ?? []).map((smartlist) => ({
        ...smartlist,
        status: ItemStatus.getSmartlistStatus(smartlist),
        geoblock_lines: getGeoblockLines(smartlist, t, countries),
      })),
    [smartlistIndexList?.data, t, countries],
  );

  const { mutate: deleteSmartlist } = useMutation({
    mutationFn: async (uuid: string) =>
      SmartlistAPI.deleteSmartlist({ uuid, projectSlug }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getSmartlistIndexListQuery.queryKey,
      });
      toast.success(deleteCompleteText);
    },
    onError: () => {
      toast.error(t("error:genericError"));
    },
  });

  const getRowHref = useCallback(
    (smartlist: SmartlistRow): string =>
      getSmartlistHref(projectSlug, editUrlPrefix, smartlist.uuid),
    [projectSlug, editUrlPrefix],
  );

  const showsElementsType =
    Array.isArray(smartlistTypes) && smartlistTypes.length > 1;

  const columnList = useMemo(
    () =>
      smartlistColumns({
        t,
        projectSlug,
        segment: editUrlPrefix,
        showsElementsType,
        deleteTitle,
        getDeleteBody,
        onEdit: (smartlist) => history.push(getRowHref(smartlist)),
        onDelete: (smartlist) => deleteSmartlist(smartlist.uuid),
      }),
    [
      t,
      projectSlug,
      editUrlPrefix,
      showsElementsType,
      deleteTitle,
      getDeleteBody,
      history,
      getRowHref,
      deleteSmartlist,
    ],
  );

  return (
    <CappedPage contentClassName="pt-7.5 pb-15">
      <Breadcrumbs items={breadcrumbs} />
      <DataTable
        columns={columnList}
        data={smartlistRowList}
        defaultSorting={{ id: "updated_at", desc: true }}
        getRowHref={getRowHref}
        headerActions={createButton}
        isError={isError}
        isLoading={isLoading}
        onRetry={refetch}
        title={tableTitle}
      />
    </CappedPage>
  );
}

export default GenericSmartlistList;
